<template>
  <div v-if="isVisible" class="cbl-overlay" @click.self="close" role="dialog" aria-modal="true">
    <div class="cbl-dialog">

      <!-- ═══ HEADER ═══ -->
      <div class="cbl-head">
        <div class="cbl-head-left">
          <div class="cbl-head-icon"><i class="ri-delete-bin-6-line"></i></div>
          <div>
            <div class="cbl-head-title">Corbeille des commandes</div>
            <div class="cbl-head-sub">
              <span class="cbl-chip">{{ rows.length }} archive{{ rows.length > 1 ? 's' : '' }}</span>
              <span class="cbl-head-note">Rétention 30 jours</span>
            </div>
          </div>
        </div>
        <button class="cbl-close" @click="close" aria-label="Fermer"><i class="ri-close-line"></i></button>
      </div>

      <!-- ═══ BARRE OUTILS ═══ -->
      <div class="cbl-toolbar">
        <div class="cbl-search-wrap">
          <i class="ri-search-line cbl-search-icon"></i>
          <input v-model="search" type="search" class="cbl-search"
                 placeholder="Rechercher par n° local, n° serveur, message d'erreur..." />
        </div>
        <select v-model="filterReason" class="cbl-sel">
          <option value="">Toutes les causes</option>
          <option v-for="r in reasonsPresentes" :key="r" :value="r">{{ reasonLabel(r) }}</option>
        </select>
        <select v-model="filterStatus" class="cbl-sel">
          <option value="">Tous statuts</option>
          <option value="pending">En attente</option>
          <option value="sent">Envoyée</option>
          <option value="failed">Erreur</option>
          <option value="completed">Comptabilisée</option>
        </select>
        <button class="cbl-btn-sec" @click="load" :disabled="isLoading">
          <i :class="isLoading ? 'ri-loader-4-line cbl-spin' : 'ri-refresh-line'"></i> Rafraîchir
        </button>
      </div>

      <!-- ═══ RÉPARTITION PAR CAUSE ═══ -->
      <div class="cbl-stats">
        <span v-for="(n, r) in comptageParCause" :key="r" class="cbl-stat">
          <span class="cbl-stat-dot" :class="'cbl-dot-' + r"></span>
          {{ reasonLabel(r) }} : <strong>{{ n }}</strong>
        </span>
        <span v-if="!rows.length" class="cbl-stat cbl-stat-empty">Aucune archive</span>
      </div>

      <!-- ═══ BODY ═══ -->
      <div class="cbl-body">
        <div v-if="isLoading" class="cbl-state">
          <i class="ri-loader-4-line cbl-spin cbl-state-icon"></i>
          <p>Lecture de la corbeille…</p>
        </div>

        <table v-else class="cbl-table">
          <thead>
            <tr>
              <th class="cbl-th cbl-th-num">N°</th>
              <th class="cbl-th">N° local</th>
              <th class="cbl-th">N° serveur</th>
              <th class="cbl-th cbl-th-center">Statut</th>
              <th class="cbl-th">Cause de suppression</th>
              <th class="cbl-th">Supprimée le</th>
              <th class="cbl-th">Message</th>
              <th class="cbl-th cbl-th-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="filtered.length">
              <tr v-for="(r, i) in filtered" :key="r.id" class="cbl-tr">
                <td class="cbl-td"><span class="cbl-num">{{ i + 1 }}</span></td>
                <td class="cbl-td"><span class="cbl-mono">{{ r.localId || '-' }}</span></td>
                <td class="cbl-td">
                  <span v-if="r.srvdocnum" class="cbl-mono">{{ r.srvdocnum }}</span>
                  <span v-else class="cbl-dash">jamais transmise</span>
                </td>
                <td class="cbl-td cbl-td-center">
                  <span class="cbl-badge" :class="'cbl-st-' + r.status">{{ statusLabel(r.status) }}</span>
                </td>
                <td class="cbl-td">
                  <span class="cbl-reason" :class="'cbl-rs-' + r.reason">{{ reasonLabel(r.reason) }}</span>
                </td>
                <td class="cbl-td cbl-muted">{{ formatDate(r.deletedAt) }}</td>
                <td class="cbl-td cbl-muted cbl-msg" :title="r.errorMessage">{{ r.errorMessage || '-' }}</td>
                <td class="cbl-td cbl-td-center">
                  <button class="cbl-restore" @click="restaurer(r)"
                          :disabled="restoringId === r.id || !restaurable(r)"
                          :title="motifNonRestaurable(r) || 'Remettre cette commande dans la file d\'attente'">
                    <i :class="restoringId === r.id ? 'ri-loader-4-line cbl-spin' : 'ri-arrow-go-back-line'"></i>
                    Restaurer
                  </button>
                  <div v-if="!restaurable(r)" class="cbl-norestore">
                    <i class="ri-lock-line"></i> {{ motifNonRestaurable(r) }}
                  </div>
                </td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="8">
                <div class="cbl-state">
                  <i class="ri-inbox-line cbl-state-icon"></i>
                  <p>{{ rows.length ? 'Aucun résultat pour ces filtres' : 'La corbeille est vide' }}</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ═══ FOOTER ═══ -->
      <div class="cbl-foot">
        <div class="cbl-foot-info">
          {{ filtered.length }} affichée{{ filtered.length > 1 ? 's' : '' }} sur {{ rows.length }}
        </div>
        <div class="cbl-foot-actions">
          <button class="cbl-btn-warn" @click="purger" :disabled="isLoading">
            <i class="ri-brush-line"></i> Purger au-delà de 30 jours
          </button>
          <button class="cbl-btn-sec" @click="close">Fermer</button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { getTrash, purgeTrash, restoreFromTrash } from '@/service/orderQueueService';

export default {
  /* eslint-disable */
  name: 'modal_corbeillecommandes',

  data() {
    return {
      isVisible: false,
      isLoading: false,
      rows: [],
      search: '',
      filterReason: '',
      filterStatus: '',
      restoringId: null,
    };
  },

  computed: {
    reasonsPresentes() {
      return Array.from(new Set(this.rows.map(r => r.reason).filter(Boolean))).sort();
    },

    comptageParCause() {
      return this.rows.reduce((acc, r) => {
        const k = r.reason || 'inconnue';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {});
    },

    filtered() {
      let list = this.rows;
      if (this.filterReason) list = list.filter(r => r.reason === this.filterReason);
      if (this.filterStatus) list = list.filter(r => r.status === this.filterStatus);

      const q = this.search.trim().toLowerCase();
      if (!q) return list;
      return list.filter(r =>
        String(r.localId      || '').toLowerCase().includes(q) ||
        String(r.srvdocnum    || '').toLowerCase().includes(q) ||
        String(r.serverDocId  || '').toLowerCase().includes(q) ||
        String(r.errorMessage || '').toLowerCase().includes(q)
      );
    },
  },

  methods: {
    show() {
      this.isVisible = true;
      this.search = '';
      this.filterReason = '';
      this.filterStatus = '';
      document.body.style.overflow = 'hidden';
      this.load();
    },

    close() {
      this.isVisible = false;
      document.body.style.overflow = '';
    },

    async load() {
      this.isLoading = true;
      try {
        // Payload laissé chiffré : la corbeille sert à savoir CE QUI a été
        // supprimé et pourquoi, pas à relire le contenu des commandes.
        this.rows = await getTrash(500, false);
      } catch (e) {
        console.error('[corbeille] Lecture impossible', e);
        this.rows = [];
        toastr['error']('Lecture de la corbeille impossible.', '<b>Erreur</b>');
      } finally {
        this.isLoading = false;
      }
    },

    /* Une vente comptabilisée est définitive : la remettre dans la file
       permettrait de l'encaisser une seconde fois, donc de produire un
       doublon de règlement. La restauration lui est fermée. */
    restaurable(r) {
      return !r || r.status !== 'completed';
    },

    motifNonRestaurable(r) {
      if (r && r.status === 'completed') return 'Vente déjà comptabilisée';
      return '';
    },

    async restaurer(r) {
      if (!this.restaurable(r)) {
        toastr['warning'](
          'Cette commande a déjà été comptabilisée : elle ne peut pas revenir dans la file d\'attente.',
          '<b>Restauration impossible</b>'
        );
        return;
      }
      this.restoringId = r.id;
      try {
        const res = await restoreFromTrash(r.id);
        if (res.success) {
          toastr['success']('Commande ' + (r.localId || '') + ' remise dans la file.', '<b>Restaurée</b>');
          this.$emit('restored', res.localId);
        } else {
          toastr['error'](res.message || 'Restauration impossible.', '<b>Erreur</b>');
        }
      } catch (e) {
        console.error('[corbeille] Restauration impossible', e);
        toastr['error']('Restauration impossible.', '<b>Erreur</b>');
      } finally {
        this.restoringId = null;
      }
    },

    async purger() {
      const n = await purgeTrash(30);
      toastr['info'](n + ' archive(s) supprimée(s).', '<b>Corbeille</b>');
      await this.load();
    },

    statusLabel(s) {
      return ({
        pending: 'En attente', sent: 'Envoyée',
        failed: 'Erreur', completed: 'Comptabilisée',
      })[s] || s || '-';
    },

    reasonLabel(r) {
      return ({
        'purge-refresh':        'Rafraîchissement',
        'suppression-manuelle': 'Suppression manuelle',
        'encaissement':         'Encaissement',
        'expiration-24h':       'Expiration 24 h',
        'vidage-total':         'Vidage total',
      })[r] || r || 'Inconnue';
    },

    formatDate(ts) {
      if (!ts) return '-';
      const d = new Date(ts);
      const p = (n) => String(n).padStart(2, '0');
      return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
  },

  beforeUnmount() {
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
.cbl-overlay {
  position: fixed; inset: 0; z-index: 10040;
  background: rgba(0,0,0,.55);
  display: flex; align-items: center; justify-content: center; padding: 16px;
  font-family: 'Segoe UI', system-ui, sans-serif;
}
.cbl-dialog {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 14px; width: min(1100px, 100%);
  max-height: 90dvh; display: flex; flex-direction: column;
  overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,.45);
}

.cbl-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.cbl-head-left { display: flex; align-items: center; gap: 10px; }
.cbl-head-icon {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: color-mix(in srgb, var(--red) 15%, transparent);
  color: var(--red); font-size: 18px;
  display: flex; align-items: center; justify-content: center;
}
.cbl-head-title { font-size: 14px; font-weight: 800; color: var(--text); }
.cbl-head-sub { margin-top: 2px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cbl-head-note { font-size: 10px; color: var(--text3); }
.cbl-chip {
  font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 20px;
  border: 1px solid var(--red); color: var(--red);
  background: color-mix(in srgb, var(--red) 10%, transparent);
}
.cbl-close {
  background: none; border: none; color: var(--text2); font-size: 20px;
  cursor: pointer; padding: 4px; border-radius: 6px; flex-shrink: 0;
}
.cbl-close:hover { color: var(--red); }

.cbl-toolbar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 10px 14px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.cbl-search-wrap { position: relative; flex: 1; min-width: 180px; }
.cbl-search-icon {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: var(--text3); pointer-events: none; font-size: 15px;
}
.cbl-search {
  width: 100%; padding: 9px 12px 9px 32px;
  background: var(--surface2); border: 1px solid var(--border);
  border-radius: 8px; color: var(--text); font-size: 13px;
  outline: none; font-family: inherit;
}
.cbl-search:focus { border-color: var(--amber); }
.cbl-sel {
  padding: 8px 10px; border-radius: 7px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text); font-size: 12px; outline: none; cursor: pointer; font-family: inherit;
}

.cbl-stats {
  display: flex; gap: 14px; flex-wrap: wrap;
  padding: 7px 18px; font-size: 11px; color: var(--text2);
  background: var(--surface2); border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.cbl-stat { display: inline-flex; align-items: center; gap: 5px; }
.cbl-stat-empty { color: var(--text3); }
.cbl-stat-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--text3); }
.cbl-dot-purge-refresh        { background: var(--blue, #60a5fa); }
.cbl-dot-suppression-manuelle { background: var(--red, #f87171); }
.cbl-dot-encaissement         { background: var(--green, #34d399); }
.cbl-dot-expiration-24h       { background: var(--orange, #f59e0b); }
.cbl-dot-vidage-total         { background: var(--purple, #a78bfa); }

.cbl-body { flex: 1; overflow: auto; }
.cbl-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 20px; text-align: center; gap: 10px;
}
.cbl-state-icon { font-size: 40px; color: var(--text3); }
.cbl-state p { font-size: 14px; font-weight: 600; color: var(--text2); }
.cbl-spin { animation: cbl-rot .9s linear infinite; display: inline-block; }
@keyframes cbl-rot { to { transform: rotate(360deg); } }

.cbl-table { width: 100%; border-collapse: collapse; }
.cbl-th {
  padding: 10px 12px; text-align: left; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .5px; color: var(--text2);
  background: color-mix(in srgb, var(--red) 8%, var(--surface2));
  border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 1;
  white-space: nowrap;
}
.cbl-th-num { width: 40px; }
.cbl-th-center { text-align: center; }
.cbl-tr { border-bottom: 1px solid var(--border); }
.cbl-tr:hover { background: color-mix(in srgb, var(--red) 5%, var(--surface)); }
.cbl-td { padding: 9px 12px; font-size: 12px; color: var(--text); vertical-align: middle; }
.cbl-td-center { text-align: center; }
.cbl-num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%; font-size: 10px; font-weight: 800;
  background: var(--surface2); color: var(--text2);
}
.cbl-mono { font-family: ui-monospace, monospace; font-size: 11px; font-weight: 700; }
.cbl-muted { font-size: 11px; color: var(--text2); }
.cbl-dash { color: var(--text3); font-size: 11px; font-style: italic; }
/* Le message d'erreur est la seule information qui explique la mise en
   corbeille : il doit être lisible en entier, pas coupé à 220 px. On le laisse
   revenir à la ligne et on coupe les chaînes sans espace (pid, SQL). */
.cbl-msg {
  max-width: 380px;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.45;
}
.cbl-table td { vertical-align: top; }

.cbl-badge {
  display: inline-block; padding: 2px 9px; border-radius: 20px;
  font-size: 10px; font-weight: 700; white-space: nowrap;
}
.cbl-st-pending   { color: var(--orange, #f59e0b); background: color-mix(in srgb, var(--orange, #f59e0b) 12%, transparent); }
.cbl-st-sent      { color: var(--blue, #60a5fa);   background: color-mix(in srgb, var(--blue, #60a5fa) 12%, transparent); }
.cbl-st-failed    { color: var(--red, #f87171);    background: color-mix(in srgb, var(--red, #f87171) 12%, transparent); }
.cbl-st-completed { color: var(--green, #34d399);  background: color-mix(in srgb, var(--green, #34d399) 12%, transparent); }

.cbl-reason { font-size: 11px; font-weight: 700; }
.cbl-rs-purge-refresh        { color: var(--blue, #60a5fa); }
.cbl-rs-suppression-manuelle { color: var(--red, #f87171); }
.cbl-rs-encaissement         { color: var(--green, #34d399); }
.cbl-rs-expiration-24h       { color: var(--orange, #f59e0b); }
.cbl-rs-vidage-total         { color: var(--purple, #a78bfa); }

.cbl-restore {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 11px; border-radius: 7px; border: none; cursor: pointer;
  background: color-mix(in srgb, var(--green, #34d399) 15%, transparent);
  color: var(--green, #34d399); font-size: 11px; font-weight: 700; font-family: inherit;
}
.cbl-restore:hover:not(:disabled) { background: color-mix(in srgb, var(--green, #34d399) 25%, transparent); }
.cbl-restore:disabled { opacity: .4; cursor: not-allowed; }

.cbl-norestore {
  margin-top: 4px; font-size: 10px; font-weight: 600;
  color: var(--txt-muted, #94a3b8); white-space: nowrap;
}

.cbl-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 14px; border-top: 1px solid var(--border); flex-shrink: 0;
  background: color-mix(in srgb, var(--surface2) 50%, var(--surface)); flex-wrap: wrap;
}
.cbl-foot-info { font-size: 12px; color: var(--text2); }
.cbl-foot-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.cbl-btn-sec, .cbl-btn-warn {
  padding: 7px 14px; border-radius: 8px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text2); font-size: 12px; font-weight: 600;
  cursor: pointer; font-family: inherit;
}
.cbl-btn-sec:hover { border-color: var(--amber); color: var(--amber); }
.cbl-btn-warn { color: var(--orange, #f59e0b); }
.cbl-btn-warn:hover:not(:disabled) { border-color: var(--orange, #f59e0b); }
.cbl-btn-warn:disabled { opacity: .4; cursor: not-allowed; }

@media (max-width: 640px) {
  .cbl-overlay { align-items: flex-end; padding: 0; }
  .cbl-dialog { width: 100%; max-height: 94dvh; border-radius: 16px 16px 0 0; }
}
</style>
